"use client"

import { useState, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { useNavigate, useLocation } from "react-router-dom"
import { GoogleAuthProvider } from "firebase/auth"
import "../../styles/LoadingSpinner.css"
import "./SignUp.css"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { useAuth } from "../../utils/AuthContext"
import Spinner from "../Spinner/Spinner"
import { useLocations } from "../../utils/LocationContext"
import FloatingField from "../FloatingField/FloatingField"
import LegalModal from "../LegalAspects/LegalModal"
import googleLogo from "../../assets/glogo.webp"
import LocationTermsModal from "../LocationTermsModal/LocationTermsModal"
import {getUserData} from "../../utils/auth";
import { useTheme } from "../../utils/ThemeContext"
import GeolocationField from "../GeolocationField/GeolocationField"
import { buildLocationPayload } from "../../services/usersApi"

const SignUp = () => {
    const { handleSignUp, handleGoogleSignIn } = useAuth()
    const { theme } = useTheme() // Obtenemos el tema actual
    const [registerMethod, setRegisterMethod] = useState("manual") // "manual" o "google"
    const [loadingGoogle, setLoadingGoogle] = useState(false)
    const [formData, setFormData] = useState({
        email: "",
        name: "",
        birthDate: "",
        location: "",
        locationCoordinates: null, // Nuevo campo para coordenadas
        customLocation: "",
        password: "",
        confirmPassword: "",
        isProvider: false,
        providerType: "",
        termsAccepted: false
    })
    const { locationNames } = useLocations()
    const locations = locationNames
    const [loading, setLoading] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [showCustomLocation, setShowCustomLocation] = useState(false) // Estado para controlar la visualización del campo personalizado
    const [showTermsModal, setShowTermsModal] = useState(false) // Estado para controlar la visualización del modal de términos
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [mustCompleteInfo, setMustCompleteInfo] = useState(false);
    const navigate = useNavigate()
    const locationHook = useLocation()
    const provider = new GoogleAuthProvider()

    useEffect(() => {
        if (locationHook.state?.providerType) {
            setFormData((prevData) => ({
                ...prevData,
                isProvider: true,
                providerType: locationHook.state.providerType
            }))
        }
    }, [locationHook.state])

    useEffect(() => {
        const user = getUserData();
        if (user) {
            if (!user.location || user.termsAccepted === false || user.termsAccepted === null) {
                setShowLocationModal(true);
                setMustCompleteInfo(true);
            } else {
                navigate("/");
            }
        }
    }, [navigate]);

    const validatePassword = () => {
        const errors = []
        if (formData.password.length < 6) errors.push("Debe tener al menos 6 caracteres")
        if (!/[0-9]/.test(formData.password)) errors.push("Debe contener al menos un número")
        if (!/[a-z]/.test(formData.password)) errors.push("Debe contener al menos una letra minúscula")
        return errors
    }

    const handleSubmit = async () => {
        if (!formData.termsAccepted) {
            toast.error("Debes aceptar los términos y condiciones para continuar.")
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error("El correo electrónico no tiene un formato válido.")
            return;
        }

        if (!formData.birthDate || new Date(formData.birthDate) >= new Date()) {
            toast.error("La fecha de nacimiento debe ser anterior a la fecha actual.")
            return;
        }

        setLoading(true)

        const birthDate = new Date(formData.birthDate);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const hasBirthdayPassed =
            today.getMonth() > birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

        const effectiveAge = hasBirthdayPassed ? age : age - 1;

        if (effectiveAge < 13) {
            toast.error("Debes tener al menos 13 años para registrarte.");
            return;
        }

        const passwordErrors = validatePassword()
        if (passwordErrors.length > 0) {
            toast.error(`Errores en la contraseña: ${passwordErrors.join(", ")}`)
            setLoading(false)
            return
        }

        if (formData.password !== formData.confirmPassword) {
            toast.error("Las contraseñas no coinciden.")
            setLoading(false)
            return
        }

        try {

            // Construir payload de ubicación según nueva arquitectura
            const finalLocationName = formData.location === "custom" ? formData.customLocation : formData.location

            // Determinar el source según cómo se obtuvo la ubicación
            let smartLocation = null;

            if (formData.locationCoordinates) {

                smartLocation = {
                    displayName: finalLocationName,
                    coordinates: {
                        lat: formData.locationCoordinates.latitude,
                        lng: formData.locationCoordinates.longitude
                    },
                    source: formData.locationCoordinates.type === 'approximate' ? 'GPS' : 'GPS',
                    isApproximate: formData.locationCoordinates.type === 'approximate'
                };

            } else if (formData.location && formData.location !== "custom") {

                // Buscar el ID de la ubicación en la lista disponible
                const selectedLocationObj = locations.find(loc =>
                    (typeof loc === 'string' ? loc : loc.name) === formData.location
                );

                if (selectedLocationObj && selectedLocationObj.id) {
                    smartLocation = {
                        displayName: finalLocationName,
                        serviceLocationId: selectedLocationObj.id,
                        source: "ZONE_SELECTION",
                        isApproximate: true
                    };
                } else {
                    // Si no encuentra ID, tratar como ubicación libre
                    smartLocation = {
                        displayName: finalLocationName,
                        source: "CUSTOM_INPUT",
                        isApproximate: true
                    };
                }

            } else if (formData.location === "custom" || finalLocationName) {

                smartLocation = {
                    displayName: finalLocationName,
                    source: "CUSTOM_INPUT",
                    isApproximate: true
                };

            }

            // Preparar datos completos para registro según formato del backend
            const submissionData = {
                name: formData.name,
                email: formData.email,
                birthDate: formData.birthDate,
                password: formData.password,
                isProvider: formData.isProvider,
                providerType: formData.providerType,
                termsAccepted: true
            };

            // Solo agregar smartLocation si existe
            if (smartLocation) {
                submissionData.smartLocation = smartLocation;
            } else {
            }


            // Realizar el registro y obtener datos completos del usuario
            const completeUserData = await handleSignUp(submissionData)

            // Verificar si los datos están completos, si no, mostrar modal de completar información
            if (!completeUserData.location || !completeUserData.termsAccepted) {
                setShowLocationModal(true);
                setMustCompleteInfo(true);
                return; // No continuar con la navegación hasta completar datos
            }

            // Continuar con navegación según el tipo de usuario
            if (locationHook.state?.providerType) {
                if (locationHook.state.providerType === "service") {
                    navigate("/registro/servicios")
                } else if (locationHook.state.providerType === "products") {
                    navigate("/registro/productos")
                }
            } else if (formData.isProvider) {
                navigate("/registro/proveedor", { state: { providerType: formData.providerType } })
            } else {
                navigate("/")
            }
        } catch (error) {
            console.error("❌ Signup - ERROR COMPLETO EN REGISTRO:");
            console.error("📍 Signup - Tipo de error:", error.name);
            console.error("📝 Signup - Mensaje:", error.message);
            console.error("📊 Signup - Stack trace:", error.stack);
            console.error("🕐 Signup - Timestamp error:", new Date().toISOString());
            console.error("Error en el registro:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        })
    }

    const handleLocationChange = (location) => {

        // 🔧 MANTENER las coordenadas existentes al cambiar la ubicación
        setFormData(prevFormData => {
            const newFormData = { ...prevFormData, location }
            return newFormData
        })
    }

    const handleCoordinatesChange = (coordinates) => {


        // 🔧 USAR prevFormData para evitar sobreescribir otros campos
        setFormData(prevFormData => {
            const newFormData = { ...prevFormData, locationCoordinates: coordinates }
            return newFormData
        })
    }

    // Función para abrir el modal de términos y condiciones
    const openTermsModal = (e) => {
        e.preventDefault()
        setShowTermsModal(true)
    }

    const handleToggleChange = (method) => {
        setRegisterMethod(method)
    }

    const handleLocationModalSuccess = () => {
        setShowLocationModal(false);
        setMustCompleteInfo(false);

        // Aplicamos la misma lógica de redirección que en onGoogleSignUp y handleSubmit
        if (locationHook.state?.providerType) {
            if (locationHook.state.providerType === "service") {
                navigate("/registro/servicios")
            } else if (locationHook.state.providerType === "products") {
                navigate("/registro/productos")
            }
        } else if (formData.isProvider) {
            navigate("/registro/proveedor")
        } else {
            navigate("/")
        }
    };

    const handleLocationModalClose = () => {
        if (mustCompleteInfo) {
            toast.warning("Debes completar tu información antes de continuar.");
        } else {
            setShowLocationModal(false);
        }
    };

    // Función para manejar el registro con Google
    const onGoogleSignUp = async (e) => {
        e.preventDefault()
        setLoadingGoogle(true)
        try {
            const locationRequired = await handleGoogleSignIn(provider)
            if (locationRequired) {
                toast.info("Por favor, completa tu localidad y acepta los términos para continuar.")
                setShowLocationModal(true);
                setMustCompleteInfo(true);
            } else {
                // Redirección especial si venía de ProviderSelection con providerType
                if (locationHook.state?.providerType) {
                    if (locationHook.state.providerType === "service") {
                        navigate("/registro/servicios")
                    } else if (locationHook.state.providerType === "products") {
                        navigate("/registro/productos")
                    }
                } else if (formData.isProvider) {
                    navigate("/registro/proveedor", { state: { providerType: formData.providerType } })
                } else {
                    navigate("/")
                }
            }
        } catch (error) {
            console.error("Error al registrarse con Google:", error)
        } finally {
            setLoadingGoogle(false)
        }
    }

    return (
        <div className={`container ${theme === 'light' ? 'light-theme' : ''}`}>
            <Helmet>
                <title>Registrate Gratis | ContactaMe</title>
                <meta
                    name="description"
                    content="Creá tu cuenta gratuita en ContactaMe. Registrate para ofrecer tus servicios o productos, o para encontrar profesionales cerca tuyo. Registro con email o Google."
                />
                <meta
                    name="keywords"
                    content="registrarse, crear cuenta, sign up, contactame, registro gratis, alta proveedor"
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://contactame.com.ar/registro" />
                <meta property="og:title" content="Registrate Gratis | ContactaMe" />
                <meta property="og:description" content="Creá tu cuenta gratuita en ContactaMe. Ofrecé tus servicios o encontrá profesionales." />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:url" content="https://contactame.com.ar/registro" />
                <meta name="twitter:title" content="Registrate Gratis | ContactaMe" />
                <meta name="twitter:description" content="Creá tu cuenta gratuita en ContactaMe." />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://contactame.com.ar/registro" />

                {/* Robots - no indexar páginas de registro */}
                <meta name="robots" content="noindex, follow" />
            </Helmet>

            {(loading || loadingGoogle) && <Spinner />}
            <div className={`card ${registerMethod === 'manual' ? 'card-manual' : 'card-google'}`}>
                <h2>Información Personal</h2>

                {/* Toggle de método de registro mejorado */}
                <div className="signup-method-toggle">
                    <div
                        className={`signup-method-option ${registerMethod === "manual" ? "active" : ""}`}
                        onClick={() => handleToggleChange("manual")}
                    >
                        <span className="method-toggle-text">Registro manual</span>
                    </div>
                    <div
                        className={`signup-method-option ${registerMethod === "google" ? "active" : ""}`}
                        onClick={() => handleToggleChange("google")}
                    >
                        <img src={googleLogo} alt="Google" className="method-google-logo" />
                        <span className="method-toggle-text">Con Google</span>
                    </div>
                </div>

                {registerMethod === "manual" ? (
                    <>
                        <form>
                            <FloatingField value={formData.name} label="Nombre Completo" id="name">
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    className="form-control"
                                    placeholder=" "
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </FloatingField>

                            <FloatingField value={formData.email} label="Correo Electrónico" id="email">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="form-control"
                                    placeholder=" "
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </FloatingField>

                            <FloatingField value={formData.birthDate} label="Fecha de Nacimiento" id="birthDate"
                                           inputType="date">
                                <input
                                    id="birthDate"
                                    name="birthDate"
                                    type="date"
                                    className="form-control"
                                    value={formData.birthDate}
                                    onChange={handleChange}
                                    required
                                />
                            </FloatingField>

                            <GeolocationField
                                value={formData.location}
                                onChange={handleLocationChange}
                                onCoordinatesChange={handleCoordinatesChange}
                                label="Ubicación"
                                required={true}
                                allowCustom={true}
                                showCoordinatesOption={false}
                            />

                            {showCustomLocation && (
                                <FloatingField value={formData.customLocation} label="Especificar ubicación" id="customLocation">
                                    <input
                                        id="customLocation"
                                        name="customLocation"
                                        type="text"
                                        className="form-control"
                                        placeholder=" "
                                        value={formData.customLocation}
                                        onChange={handleChange}
                                        required
                                    />
                                </FloatingField>
                            )}

                            <FloatingField
                                value={formData.password}
                                label="Contraseña"
                                id="password"
                                isPassword={true}
                                onToggle={() => setShowPassword(!showPassword)}
                                toggleIcon={showPassword ? <FaEyeSlash/> : <FaEye/>}
                            >
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    placeholder=" "
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                />
                            </FloatingField>
                            <p style={{fontSize: "0.7rem", marginTop:"-2rem", marginBottom:"1rem", alignSelf:"center"}}>Mínimo 6 caracteres, con un número y una letra minúscula.</p>

                            <FloatingField
                                value={formData.confirmPassword}
                                label="Confirmar Contraseña"
                                id="confirmPassword"
                                isPassword={true}
                                onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                                toggleIcon={showConfirmPassword ? <FaEyeSlash/> : <FaEye/>}
                            >
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="form-control"
                                    placeholder=" "
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    required
                                />
                            </FloatingField>

                            <div className="checkbox-wrapper">
                                <input
                                    type="checkbox"
                                    id="isProvider"
                                    name="isProvider"
                                    checked={formData.isProvider}
                                    onChange={handleChange}
                                />
                                <label htmlFor="isProvider">Quiero ofrecer un servicio o producto</label>
                            </div>

                            <div className="checkbox-wrapper terms-checkbox-signup">
                                <input
                                    type="checkbox"
                                    id="termsAccepted"
                                    name="termsAccepted"
                                    checked={formData.termsAccepted}
                                    onChange={handleChange}
                                    required
                                />
                                <label htmlFor="termsAccepted">
                                    Acepto los <a href="#" onClick={openTermsModal} className="terms-link-signup"><b>términos y condiciones</b></a>
                                </label>
                            </div>
                        </form>
                        <button
                            className="new-btn btn-red"
                            onClick={handleSubmit}
                            disabled={loading || !formData.termsAccepted}
                        >
                            {loading ? "Cargando..." : "Registrarme"}
                        </button>
                    </>
                ) : (
                    <div className="google-signup-container">
                        <div className="checkbox-wrapper" style={{marginBottom: "15px", fontSize: "0.9rem"}}>
                            <input
                                type="checkbox"
                                id="isProviderGoogle"
                                name="isProvider"
                                checked={formData.isProvider}
                                onChange={handleChange}
                            />
                            <label htmlFor="isProviderGoogle">Quiero ofrecer un servicio o producto</label>
                        </div>

                        <p style={{fontSize:"0.9rem", textAlign:"center", marginBottom:"0px"}}>Regístrate usando tu cuenta de Google</p>

                        <button
                            className="new-btn btn-outline-gray google-button"
                            onClick={onGoogleSignUp}
                            disabled={loadingGoogle}
                        >
                            <img src={googleLogo || "/placeholder.svg"} alt="Google Logo" className="google-logo" />
                            {loadingGoogle ? "Registrando..." : "Registrarme con Google"}
                        </button>
                    </div>
                )}

                <div className="login-prompt-container" onClick={() => navigate("/login")}>
                    <p className="login-prompt-text">
                        ¿Ya tenés cuenta? <a href="/login" className="login-prompt-link">Inicia sesión</a>
                    </p>
                </div>
            </div>

            {/* Modal de términos y condiciones */}
            <LegalModal
                isOpen={showTermsModal}
                onClose={() => setShowTermsModal(false)}
                title="Términos y Condiciones"
                contentType="terms"
            />

            <LocationTermsModal
                isOpen={showLocationModal}
                onClose={handleLocationModalClose}
                onSuccess={handleLocationModalSuccess}
                isMandatory={mustCompleteInfo}
            />

        </div>
    )
}

export default SignUp;
